import React, { useState, MouseEvent, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Row, Col } from "react-bootstrap";
import { DeviceBase } from "@preveil-api";
import {
  Account, AppConfiguration, Device, DevicePlatform, DeviceStatus, LockDeviceApiResponse, Message, MessageHandlerDisplayType, MessageToastTypes, OrgUserRoles,
  PublicRoutes, useAppDispatch, useDeleteUsersDevicesKeysByDeviceIdMutation, useLockDeviceMutation, useRemoveAccountFromCurrentDeviceMutation, useUnlockDeviceMutation, QueryParamKeys, getDeviceStatus, AdminMessages, MessageAnchors,
  DeviceAction, AdminErrorMessages, AdminSuccessMessages
} from "src/common";
import { Icon } from "src/components";
import { DeviceConfirmation, RenameForm } from ".";
import { uiActions, accountActions } from "src/store";

type AllProps = {
  device: DeviceBase;
  current_account: Account;
  devices: DeviceBase[];
  setDevices: Dispatch<SetStateAction<DeviceBase[]>>;
};

function DeviceRowComponent(props: AllProps) {
  const { device, current_account, devices, setDevices } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [editing_device, setEditingDevice] = useState<DeviceBase | undefined>();
  const [modal_show, setModalShow] = useState<boolean>(false);
  const current_device_id = current_account.current_device_id;
  const device_status = getDeviceStatus(device, current_device_id);
  const show_locked_admin_label = !!lockedByAdmin() && device_status.class === DeviceStatus.locked.class; // in the case when the device is locked by admin then we need to display a different label for the device status (Locked by Admin).
  const device_status_action = device.is_active ? DeviceAction.lock : DeviceAction.unlock;
  const device_status_label = device.is_active ? "Lock" : "Unlock";

  
  const [lockDevice] = useLockDeviceMutation();
  const [unlockDevice] = useUnlockDeviceMutation();
  const [disconnectDevice] = useRemoveAccountFromCurrentDeviceMutation();
  const [deleteDevice] = useDeleteUsersDevicesKeysByDeviceIdMutation();


  /* Description: Locks or Unlocks Device depending on the action. */
  async function lockOrUnlockDevice(device_action: string) {
    let query;
    if (device_action === DeviceAction.lock) {
      query = lockDevice({ userId: current_account.user_id, deviceId: device.device_id });
    } else {
      query = unlockDevice({ userId: current_account.user_id, deviceId: device.device_id });
    }
    query
      .unwrap()
      .then((response: LockDeviceApiResponse) => {
        const message = device_action === DeviceAction.lock ? AdminSuccessMessages.success_locked_device.replace(MessageAnchors.user_id, device.device_name) : AdminSuccessMessages.success_unlocked_device.replace(MessageAnchors.user_id, device.device_name);
        const devs = devices.filter((d) => d.device_id !== response.device_id);
        devs.push(response);
        setDevices(devs);
        dispatch(
          uiActions.handleSetMessage(
            new Message(message),
          ),
        );
      })
      .catch((stack) => {
        const error_message = device_action === DeviceAction.lock ? AdminErrorMessages.error_locking_device.replace(MessageAnchors.user_id, device.device_name) : AdminErrorMessages.error_unlocking_device.replace(MessageAnchors.user_id, device.device_name);
        dispatch(
          uiActions.handleRequestErrors(
            new Message(error_message),
            stack,
          ),
        );
      });
  }

  /* Description: Disconnects device and then logs out, sets current_account to undefined and redirects to the choose
  account page. */
  async function disconnectDeviceRequest() {
    disconnectDevice({ userId: current_account.user_id })
      .unwrap()
      .then(() => {
        dispatch(accountActions.revokeCurrentUser());
        // NOTE: Navigate to Logout to get full logout (store and sessions cleared)
        navigate(`/${PublicRoutes.logout_route}?${QueryParamKeys.REDIRECT_URL_QUERY_KEY}=/${PublicRoutes.choose_account_route}`);
      })
      .catch((msg) => {
        dispatch(
          uiActions.handleRequestErrors(
            new Message(
              `${msg.status === 403 ? "FORBIDDEN" : msg.status}: disconnecting device!`,
            ),
            msg,
          ),
        );
      });
  }

  /* Description: Delete the device */
  async function deleteDeviceRequest() {
    if (AppConfiguration.buildForWeb() && device.platform === DevicePlatform.session.text.toLowerCase()) {
      return;
    }
    const { device_name } = device;
    const request = {
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        device_id: device.device_id,
        user_id: current_account.user_id,
      },
    };
    deleteDevice(request)
      .unwrap()
      .then(() => {
        !AppConfiguration.buildForWeb() && setDevices(devices.filter((d) => d.device_id !== device.device_id));
        const message = AdminSuccessMessages.success_deleted_device.replace(MessageAnchors.user_id, device_name);
        dispatch(
          uiActions.handleSetMessage(
            new Message(message),
          ),
        );
      })
      .catch((msg) => {
        const message = AdminErrorMessages.error_deleting_device.replace(MessageAnchors.user_id, device_name);
        dispatch(
          uiActions.handleRequestErrors(
            new Message(message),
            msg,
          ),
        );
      });
  }

  /* Description: returns a boolean, whether the device can be unlocked or not. */
  function canBeUnlocked(lock_by_user_id: string): boolean {
    if (!!current_account.org_info && current_account.org_info.role === OrgUserRoles.admin) {
      // NOTE: admin can always unlock their device regardless of who has locked theirs.
      return true;
    }
    return current_account.user_id.toLowerCase() === lock_by_user_id.toLowerCase();
  };

  /* Description: Checks if device has been locked by admin. */
  function lockedByAdmin(): boolean {
    return (
      !device.is_active && !!device.lock_info && !canBeUnlocked(device.lock_info.lock_by_user_id)
    );
  };

  /* Description: handles the following actions: Lock, Unlock, Delete */
  function handleDeviceAction(action: string) {
    let message = "";
    let title = "";
    let button_label = "";
    switch(action) { 
      case DeviceAction.lock: { 
        message = AdminMessages.confirm_device_lock.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_lock.title;
        button_label = "Lock";
        break; 
      } 
      case DeviceAction.unlock: { 
        message = AdminMessages.confirm_device_unlock.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_unlock.title;
        button_label = "Unlock";
        break; 
      }
      case DeviceAction.delete: {
        message = AdminMessages.confirm_device_delete.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_delete.title;
        button_label = "Delete";
        break;
      }
    }
    const handleDeviceRequest = action === DeviceAction.delete ? deleteDeviceRequest : lockOrUnlockDevice;
    const confirmation_dialog = new Message(
      message,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      title,
      {
        label: button_label,
        data: true,
        action: () => handleDeviceRequest(action),
      },
      { label: "Cancel" },
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  return (
    <>
      <DeviceConfirmation
        modal_show={modal_show}
        setModalShow={setModalShow}
        func={disconnectDeviceRequest} />
      <Row>
        <Col sm={4} md={6} className="align-center ps-0">
          <Icon
            className={`pv-icon-${DevicePlatform[device.platform].class || DevicePlatform.default.class
              } device-icon`}
          ></Icon>
          {!!editing_device && editing_device.device_id === device.device_id && (
            <RenameForm
              device={device}
              current_account={current_account}
              setEditingDevice={setEditingDevice}
              devices={devices}
              setDevices={setDevices}
            ></RenameForm>
          )}
          {!editing_device && <div onClick={() => setEditingDevice(device)}>{device.device_name}</div>}
        </Col>
        <Col sm={3} md={4}>
          <Button
            variant="transparent"
            className={`notification-count ${Device.deviceStatusColor(device_status.class)}`}
            aria-label="Close"
          >
            {device_status.text}
            { show_locked_admin_label && <span>&nbsp;by Admin</span>}
          </Button>
        </Col>
        <Col sm="auto" className="ms-auto">
          <Dropdown as="button" className="btn btn-icon btn-sm"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              e.preventDefault();
            }}>
            <Dropdown.Toggle as="span">
              <Icon className="pv-icon-more"></Icon>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item onClick={() => { setEditingDevice(device); }}>Rename</Dropdown.Item>
              {device.device_id === current_device_id ? (
                <Dropdown.Item
                  onClick={() => setModalShow(true)}
                >
                  Disconnect this device
                </Dropdown.Item>
              ) : (
                <>
                  <Dropdown.Item
                    onClick={() => handleDeviceAction(device_status_action)}>
                    {device_status_label}
                  </Dropdown.Item>              
                  <Dropdown.Item
                    onClick={() => handleDeviceAction(DeviceAction.delete)}
                  >
                    Delete
                  </Dropdown.Item>{" "}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
    </>
  );
}

export default React.memo(DeviceRowComponent);
